import {
  mapApiResourceToPreVisitAnswers,
  type PreVisitAnswer,
  type PreVisitAnswerApiResource,
} from '@/api/preVisit/preVisitApi.types';

export interface IntakeFieldApiResource {
  code: string;
  section: string;
  label_en: string;
  label_pl: string;
  field_type: string;
}

export interface IntakeField {
  code: string;
  section: string;
  labelEn: string;
  labelPl: string;
  fieldType: string;
}

export interface IntakeFieldDraft {
  code: string | null;
  section: string;
  labelEn: string;
  labelPl: string;
  fieldType: string;
}

export interface IntakeFieldsApiResource {
  fields: IntakeFieldApiResource[];
}

export interface OutcomeOptionApiResource {
  code: string;
  label_en: string;
  label_pl: string;
}

export interface OutcomeOption {
  code: string;
  labelEn: string;
  labelPl: string;
}

export interface OutcomeOptionDraft {
  code: string | null;
  labelEn: string;
  labelPl: string;
}

export interface OutcomeOptionsApiResource {
  options: OutcomeOptionApiResource[];
}

export interface FirstConsultationAnswerApiResource {
  field_code: string;
  section: string;
  label_en: string;
  label_pl: string;
  field_type: string;
  value: string;
}

export interface FirstConsultationAnswer {
  fieldCode: string;
  section: string;
  labelEn: string;
  labelPl: string;
  fieldType: string;
  value: string;
}

export interface FirstConsultationDocumentApiResource {
  id: string;
  filename: string;
  content_type: string;
  url: string;
}

export interface FirstConsultationDocument {
  id: string;
  filename: string;
  contentType: string;
  url: string;
}

export interface FirstConsultationApiResource {
  id: string;
  patient_id: string;
  appointment_id: string | null;
  therapist_notes: string | null;
  private_notes: string | null;
  shared_with_client_at: string | null;
  outcome_code: string | null;
  answers: FirstConsultationAnswerApiResource[];
  pre_visit_answers: PreVisitAnswerApiResource[];
  documents: FirstConsultationDocumentApiResource[];
  created_at: string;
  updated_at: string;
}

export interface FirstConsultation {
  id: string;
  patientId: string;
  appointmentId: string | null;
  therapistNotes: string | null;
  privateNotes: string | null;
  sharedWithClientAt: string | null;
  outcomeCode: string | null;
  answers: FirstConsultationAnswer[];
  preVisitAnswers: PreVisitAnswer[];
  documents: FirstConsultationDocument[];
  createdAt: string;
  updatedAt: string;
}

export interface FirstConsultationAnswerInput {
  fieldCode: string;
  value: string;
}

export interface FirstConsultationWritePayload {
  appointmentId: string | null;
  answers: FirstConsultationAnswerInput[];
  therapistNotes: string | null;
  privateNotes: string | null;
  outcomeCode: string | null;
}

function mapIntakeField(resource: IntakeFieldApiResource): IntakeField {
  return {
    code: resource.code,
    section: resource.section,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
    fieldType: resource.field_type,
  };
}

export function mapApiResourceToIntakeFields(resource: IntakeFieldsApiResource): IntakeField[] {
  return resource.fields.map(mapIntakeField);
}

export function mapIntakeFieldDrafts(drafts: IntakeFieldDraft[]): Record<string, unknown> {
  return {
    fields: drafts.map(function toApiField(draft) {
      return {
        code: draft.code,
        section: draft.section,
        label_en: draft.labelEn,
        label_pl: draft.labelPl,
        field_type: draft.fieldType,
      };
    }),
  };
}

function mapOutcomeOption(resource: OutcomeOptionApiResource): OutcomeOption {
  return {
    code: resource.code,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
  };
}

export function mapApiResourceToOutcomeOptions(
  resource: OutcomeOptionsApiResource
): OutcomeOption[] {
  return resource.options.map(mapOutcomeOption);
}

export function mapOutcomeOptionDrafts(drafts: OutcomeOptionDraft[]): Record<string, unknown> {
  return {
    options: drafts.map(function toApiOption(draft) {
      return { code: draft.code, label_en: draft.labelEn, label_pl: draft.labelPl };
    }),
  };
}

function mapFirstConsultationAnswer(
  resource: FirstConsultationAnswerApiResource
): FirstConsultationAnswer {
  return {
    fieldCode: resource.field_code,
    section: resource.section,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
    fieldType: resource.field_type,
    value: resource.value,
  };
}

function mapFirstConsultationDocument(
  resource: FirstConsultationDocumentApiResource
): FirstConsultationDocument {
  return {
    id: resource.id,
    filename: resource.filename,
    contentType: resource.content_type,
    url: resource.url,
  };
}

export function mapApiResourceToFirstConsultation(
  resource: FirstConsultationApiResource
): FirstConsultation {
  return {
    id: resource.id,
    patientId: resource.patient_id,
    appointmentId: resource.appointment_id,
    therapistNotes: resource.therapist_notes,
    privateNotes: resource.private_notes,
    sharedWithClientAt: resource.shared_with_client_at,
    outcomeCode: resource.outcome_code,
    answers: resource.answers.map(mapFirstConsultationAnswer),
    preVisitAnswers: mapApiResourceToPreVisitAnswers(resource.pre_visit_answers),
    documents: resource.documents.map(mapFirstConsultationDocument),
    createdAt: resource.created_at,
    updatedAt: resource.updated_at,
  };
}

export function mapFirstConsultationWritePayload(
  payload: FirstConsultationWritePayload
): Record<string, unknown> {
  return {
    appointment_id: payload.appointmentId,
    answers: payload.answers.map(function toApiAnswer(answer) {
      return { field_code: answer.fieldCode, value: answer.value };
    }),
    therapist_notes: payload.therapistNotes,
    private_notes: payload.privateNotes,
    outcome_code: payload.outcomeCode,
  };
}
